import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from './FIRST4COM.png';
import './index.css';

const API_URL = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

function Users() {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  // Form states
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/users`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setUsers(data);
      }
    } catch (err) {
      setError('Erreur de chargement des utilisateurs.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (password && !editingId) {
      const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,}$/;
      if (!strongPasswordRegex.test(password)) {
        setError('Mot de passe trop faible (8+ caractères, Maj, Min, Chiffre, Spécial).');
        return;
      }
    }

    setIsLoading(true);
    try {
      const url = editingId ? `${API_URL}/api/users/${editingId}` : `${API_URL}/api/users`;
      const method = editingId ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login, password: password || undefined })
      });
      
      const data = await response.json();
      if (data.success) {
        setSuccess(editingId ? 'Utilisateur mis à jour !' : 'Utilisateur créé !');
        setLogin('');
        setPassword('');
        setEditingId(null);
        fetchUsers();
      } else {
        setError(data.error || 'Une erreur est survenue.');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingId(user.id);
    setLogin(user.login);
    setPassword('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/users/${id}`, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) {
        setSuccess('Utilisateur supprimé.');
        fetchUsers();
      }
    } catch (err) {
      setError('Erreur lors de la suppression.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      {isLoading && <div className="global-loader-overlay"><div className="loader"></div></div>}
      
      <header className="dashboard-header">
        <div className="header-left">
          <img src={logo} alt="First4Com" className="main-logo-small" />
          <div className="v-divider"></div>
          <h1>GESTION UTILISATEURS</h1>
        </div>
        <div className="header-right">
          <button onClick={() => navigate('/products')} className="primary-btn secondary">📦 Retour Dashboard</button>
          <button onClick={() => navigate('/')} className="primary-btn logout">Déconnexion</button>
        </div>
      </header>

      <div className="dashboard-content">
        <aside className="form-box">
          <h2>{editingId ? '✍️ Modifier Utilisateur' : '➕ Ajouter un Admin'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <label>Identifiant (Login)</label>
              <input 
                type="text" 
                value={login} 
                onChange={(e) => setLogin(e.target.value)} 
                placeholder="ex: admin_2024"
                required 
              />
            </div>
            <div className="input-group">
              <label>Mot de passe {editingId && '(Laisser vide pour ne pas changer)'}</label>
              <input 
                type="password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                placeholder={editingId ? "Nouveau mot de passe..." : "Mot de passe fort requis"}
                required={!editingId}
              />
              {!editingId && (
                <div className="password-requirements">
                  <p className={password.length >= 8 ? 'met' : ''}>• 8+ caractères</p>
                  <p className={/[A-Z]/.test(password) ? 'met' : ''}>• Une majuscule</p>
                  <p className={/\d/.test(password) ? 'met' : ''}>• Un chiffre</p>
                  <p className={/[^a-zA-Z0-9]/.test(password) ? 'met' : ''}>• Caractère spécial</p>
                </div>
              )}
            </div>

            <div className="btn-group">
              <button type="submit" className="primary-btn action" disabled={isLoading}>
                {editingId ? 'Mettre à jour' : 'Créer l\'utilisateur'}
              </button>
              {editingId && (
                <button type="button" onClick={() => {setEditingId(null); setLogin(''); setPassword('');}} className="primary-btn ghost">
                  Annuler
                </button>
              )}
            </div>
          </form>

          {error && <div className="error-banner small" style={{marginTop: '1rem'}}>{error}</div>}
          {success && <div className="success-banner small" style={{marginTop: '1rem'}}>{success}</div>}
        </aside>

        <main className="table-box">
          <div className="table-header">
            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
              <div style={{width: '8px', height: '8px', borderRadius: '50%', background: 'var(--primary)'}}></div>
              <h2>Liste des Administrateurs</h2>
            </div>
          </div>
          <div style={{overflowX: 'auto'}}>
            <table className="purchases-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Identifiant</th>
                  <th style={{textAlign: 'right'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td><span style={{color: 'var(--text-muted)'}}>#{u.id}</span></td>
                    <td><strong>{u.login}</strong></td>
                    <td style={{textAlign: 'right'}}>
                      <button onClick={() => handleEdit(u)} className="action-btn edit-btn" title="Modifier">✏️</button>
                      <button onClick={() => handleDelete(u.id)} className="action-btn delete-btn" title="Supprimer">🗑️</button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan="3" style={{textAlign: 'center', padding: '3rem', color: 'var(--text-muted)'}}>Aucun utilisateur trouvé.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Users;
